import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './PickingStation.css';

// What the age counter is counting from (server decides: paid date, ship date, or order date).
function ageTooltip(order) {
  const d = order.age_basis_date
    ? new Date(order.age_basis_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  if (order.age_basis === 'paid') return `Days since paid${d ? ` (${d})` : ''}`;
  if (order.age_basis === 'ship_date') return `Days since ship date${d ? ` (${d})` : ''}`;
  return `Days since order date${d ? ` (${d})` : ''}`;
}

const INACTIVITY_TIMEOUT = 90 * 60 * 1000; // 90 minutes

function PickingStation() {
  // Auth state
  const [currentOperator, setCurrentOperator] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [showGoodbye, setShowGoodbye] = useState(false);
  const [goodbyeName, setGoodbyeName] = useState('');
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());

  // Queue state
  const [activeTab, setActiveTab] = useState('queue');
  const [queue, setQueue] = useState([]);
  const [dispatched, setDispatched] = useState([]);
  const [stats, setStats] = useState({ printed_today: 0, active_now: 0, queue_count: 0 });
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState('age_days');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterType, setFilterType] = useState('all');

  // Modal state
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [printing, setPrinting] = useState(false);

  // Load saved operator on mount
  useEffect(() => {
    const saved = localStorage.getItem('pickingOperator');
    if (saved) {
      try {
        setCurrentOperator(JSON.parse(saved));
        setLastActivityTime(Date.now());
      } catch (e) {
        localStorage.removeItem('pickingOperator');
      }
    }
  }, []);

  // Inactivity timeout
  useEffect(() => {
    if (!currentOperator) return;
    const check = setInterval(() => {
      if (Date.now() - lastActivityTime > INACTIVITY_TIMEOUT) {
        handleLogout();
      }
    }, 30000);
    return () => clearInterval(check);
  }, [currentOperator, lastActivityTime]);

  const resetActivity = useCallback(() => {
    setLastActivityTime(Date.now());
  }, []);

  // Fetch data
  const fetchQueue = useCallback(async () => {
    try {
      const [regularRes, fbaRes] = await Promise.all([
        axios.get('/api/picking/queue'),
        axios.get('/api/picking/fba-queue').catch(() => ({ data: [] })),
      ]);
      setQueue([...regularRes.data, ...(fbaRes.data || [])]);
    } catch (err) {
      console.error('Failed to fetch picking queue:', err);
    }
  }, []);

  const fetchDispatched = useCallback(async () => {
    try {
      const res = await axios.get('/api/picking/dispatched');
      setDispatched(res.data);
    } catch (err) {
      console.error('Failed to fetch dispatched tickets:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/picking/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch picking stats:', err);
    }
  }, []);

  const loadAll = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    await Promise.all([fetchQueue(), fetchDispatched(), fetchStats()]);
    if (showLoader) setLoading(false);
  }, [fetchQueue, fetchDispatched, fetchStats]);

  // Initial load + auto-refresh
  useEffect(() => {
    if (currentOperator) {
      loadAll();
      const interval = setInterval(() => loadAll(false), 60000);
      return () => clearInterval(interval);
    }
  }, [currentOperator, loadAll]);

  // === PIN AUTH ===

  const handlePinDigit = (digit) => {
    if (pinInput.length < 4) {
      const newPin = pinInput + digit;
      setPinInput(newPin);
      setPinError('');
      if (newPin.length === 4) {
        submitPin(newPin);
      }
    }
  };

  const handlePinClear = () => {
    setPinInput('');
    setPinError('');
  };

  const submitPin = async (pin) => {
    try {
      const res = await axios.post('/api/operators/pin', { pin });
      const op = res.data;
      setCurrentOperator(op);
      localStorage.setItem('pickingOperator', JSON.stringify(op));
      setLastActivityTime(Date.now());
      setShowWelcome(true);
      setTimeout(() => setShowWelcome(false), 2500);
      setPinInput('');
    } catch (err) {
      setPinError('Invalid PIN');
      setPinInput('');
    }
  };

  const handleLogout = () => {
    if (currentOperator) {
      setGoodbyeName(currentOperator.first_name);
      setShowGoodbye(true);
      setTimeout(() => {
        setShowGoodbye(false);
        setGoodbyeName('');
      }, 2000);
    }
    setCurrentOperator(null);
    localStorage.removeItem('pickingOperator');
    setPinInput('');
  };

  // === ACTIONS ===

  const handlePrint = async (order) => {
    resetActivity();
    setConfirmOrder(order);
  };

  const confirmPrint = async () => {
    if (!confirmOrder || printing) return;
    setPrinting(true);
    resetActivity();

    try {
      const printerIp = localStorage.getItem('zebraPrinterIp') || localStorage.getItem('printerIp');
      if (!printerIp) {
        alert('No printer configured. Go to Settings to set your Zebra printer IP.');
        setPrinting(false);
        return;
      }

      const dispatchBody = {
        salesOrderId: confirmOrder.sales_order_id,
        tranId: confirmOrder.tran_id,
        operatorId: currentOperator?.id,
        operatorName: currentOperator?.display_name || currentOperator?.first_name,
        printerIp,
      };
      if (confirmOrder.order_type === 'fba_inbound') {
        dispatchBody.inboundPlanId = confirmOrder.inbound_plan_id;
        dispatchBody.orderType = 'fba_inbound';
      }
      await axios.post('/api/picking/dispatch', dispatchBody);

      setConfirmOrder(null);
      await loadAll(false);
    } catch (err) {
      console.error('Failed to dispatch pick ticket:', err);
      alert(err.response?.data?.error || 'Failed to print pick ticket');
    } finally {
      setPrinting(false);
    }
  };

  const handleReturn = async (ticketId) => {
    resetActivity();
    try {
      await axios.post(`/api/picking/return/${ticketId}`, {
        reason: 'Returned to queue by picker'
      });
      await loadAll(false);
    } catch (err) {
      console.error('Failed to return ticket:', err);
      alert(err.response?.data?.error || 'Failed to return ticket');
    }
  };

  // === SORTING & FILTERING ===

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const getCategoryLabel = (cat, orderType) => {
    if (orderType === 'fba_inbound') return 'FBA Inbound';
    const labels = { 1: 'DTC', 2: 'Wholesale', 3: 'Amazon', 4: 'Faire' };
    return labels[cat] || '';
  };

  const getCategoryClass = (cat, orderType) => {
    if (orderType === 'fba_inbound') return 'fba-inbound';
    const classes = { 1: 'dtc', 2: 'wholesale', 3: 'amazon', 4: 'faire' };
    return classes[cat] || '';
  };

  const getShippableInfo = (order) => {
    const shippable = order.shippable_line_items || 0;
    const total = order.total_line_items || 0;
    if (total === 0) return { label: '-', className: 'none' };
    if (shippable === total) return { label: 'Ready', className: 'fully' };
    if (shippable > 0) return { label: `${shippable}/${total}`, className: 'partial' };
    return { label: 'None', className: 'none' };
  };

  const filteredQueue = queue.filter(order => {
    if (filterType === 'all') return true;
    if (filterType === 'dtc') return order.customer_category === 1 && order.order_type !== 'fba_inbound';
    if (filterType === 'wholesale') return order.customer_category === 2;
    if (filterType === 'fba') return order.order_type === 'fba_inbound';
    return true;
  });

  const sortedQueue = [...filteredQueue].sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    if (sortColumn === 'ship_date' || sortColumn === 'tran_date') {
      aVal = new Date(aVal || '2099-01-01');
      bVal = new Date(bVal || '2099-01-01');
    }
    if (sortColumn === 'age_days' || sortColumn === 'total_line_items' || sortColumn === 'shippable_line_items') {
      aVal = parseFloat(aVal || 0);
      bVal = parseFloat(bVal || 0);
    }
    if (typeof aVal === 'string') {
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const sortIndicator = (col) => {
    if (sortColumn !== col) return '';
    return sortDirection === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  // === RENDER ===

  // Welcome screen
  if (showWelcome && currentOperator) {
    return (
      <div className="picking-welcome-screen">
        <div className="picking-welcome-content">
          <h1 className="picking-welcome-greeting">{getGreeting()},</h1>
          <h2 className="picking-welcome-name">{currentOperator.first_name}</h2>
        </div>
      </div>
    );
  }

  // Goodbye screen
  if (showGoodbye) {
    return (
      <div className="picking-welcome-screen goodbye">
        <div className="picking-welcome-content">
          <h1 className="picking-welcome-greeting">Catch you later,</h1>
          <h2 className="picking-welcome-name">{goodbyeName}</h2>
        </div>
      </div>
    );
  }

  // PIN login
  if (!currentOperator) {
    return (
      <div className="picking-login-screen">
        <div className="picking-login-container">
          <h1 className="picking-login-title">Picking Station</h1>
          <p className="picking-login-subtitle">Scan orders, print pick tickets</p>
          <div className="picking-pin-box">
            <p className="picking-pin-instruction">Enter your PIN</p>
            <div className="picking-pin-dots">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className={`picking-pin-dot ${i < pinInput.length ? 'filled' : ''}`} />
              ))}
            </div>
            {pinError && <p className="picking-pin-error">{pinError}</p>}
            <div className="picking-pin-keypad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
                <button key={d} className="picking-pin-key" onClick={() => handlePinDigit(d.toString())}>{d}</button>
              ))}
              <button className="picking-pin-key clear-key" onClick={handlePinClear}>Clear</button>
              <button className="picking-pin-key" onClick={() => handlePinDigit('0')}>0</button>
              <div className="picking-pin-key spacer-key" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="picking-station">
      <div className="picking-dashboard" onClick={resetActivity}>
        {/* Header */}
        <div className="picking-header">
          <div className="picking-header-left">
            <span className="picking-header-title">Picking Station</span>
            <span className="picking-operator-name">{currentOperator.first_name}</span>
          </div>
          <div className="picking-header-actions">
            <button className="picking-logout-btn" onClick={handleLogout}>Sign Out</button>
          </div>
        </div>

        {/* Stats */}
        <div className="picking-stats-bar">
          <div className="picking-stat-card">
            <span className="picking-stat-value">{stats.printed_today || 0}</span>
            <span className="picking-stat-label">Printed Today</span>
          </div>
          <div className="picking-stat-card">
            <span className="picking-stat-value">{stats.active_now || 0}</span>
            <span className="picking-stat-label">Active Now</span>
          </div>
          <div className="picking-stat-card">
            <span className="picking-stat-value">{stats.queue_count || 0}</span>
            <span className="picking-stat-label">In Queue</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="picking-tabs-header">
          <div className="picking-tabs-nav">
            <button
              className={`picking-tab-btn ${activeTab === 'queue' ? 'active' : ''}`}
              onClick={() => setActiveTab('queue')}
            >
              Queue
              <span className="picking-tab-badge">{filteredQueue.length}</span>
            </button>
            <button
              className={`picking-tab-btn ${activeTab === 'dispatched' ? 'active' : ''}`}
              onClick={() => setActiveTab('dispatched')}
            >
              Dispatched
              <span className="picking-tab-badge">{dispatched.length}</span>
            </button>
          </div>
        </div>

        {/* Queue Tab */}
        {activeTab === 'queue' && (
          <>
            <div className="picking-filters">
              {['all', 'dtc', 'wholesale', 'fba'].map(f => (
                <button
                  key={f}
                  className={`picking-filter-btn ${filterType === f ? 'active' : ''}`}
                  onClick={() => setFilterType(f)}
                >
                  {f === 'all' ? 'All' : f === 'dtc' ? 'DTC' : f === 'wholesale' ? 'Wholesale' : 'FBA Inbound'}
                </button>
              ))}
            </div>
            <div className="picking-table-container">
              {loading ? (
                <div className="picking-loading">Loading queue...</div>
              ) : sortedQueue.length === 0 ? (
                <div className="picking-empty">
                  <p className="picking-empty-message">No orders in queue</p>
                  <p className="picking-empty-hint">All orders have been dispatched or the cache is empty</p>
                </div>
              ) : (
                <table className="picking-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('tran_id')}>Order{sortIndicator('tran_id')}</th>
                      <th onClick={() => handleSort('customer_name')}>Customer{sortIndicator('customer_name')}</th>
                      <th onClick={() => handleSort('customer_category')}>Type{sortIndicator('customer_category')}</th>
                      <th onClick={() => handleSort('total_line_items')}>Items{sortIndicator('total_line_items')}</th>
                      <th onClick={() => handleSort('shippable_line_items')}>Shippable{sortIndicator('shippable_line_items')}</th>
                      <th onClick={() => handleSort('ship_date')}>Ship Date{sortIndicator('ship_date')}</th>
                      <th onClick={() => handleSort('age_days')}>Age{sortIndicator('age_days')}</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedQueue.map(order => {
                      const shippable = getShippableInfo(order);
                      const ageDays = order.age_days || 0;
                      const ageClass = ageDays >= 7 ? 'urgent' : ageDays >= 3 ? 'warning' : '';
                      return (
                        <tr key={order.sales_order_id}>
                          <td className="picking-order-number">{order.tran_id}</td>
                          <td className="picking-customer-name">{order.customer_name}</td>
                          <td>
                            {(order.customer_category || order.order_type === 'fba_inbound') ? (
                              <span className={`picking-category-badge ${getCategoryClass(order.customer_category, order.order_type)}`}>
                                {getCategoryLabel(order.customer_category, order.order_type)}
                              </span>
                            ) : '-'}
                          </td>
                          <td>{order.total_line_items || 0}</td>
                          <td>
                            <span className={`picking-shippable-badge ${shippable.className}`}>
                              {shippable.label}
                            </span>
                          </td>
                          <td>{order.ship_date ? new Date(order.ship_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}</td>
                          <td>
                            <span className={`picking-age ${ageClass}`} title={ageTooltip(order)}>{ageDays}d</span>
                          </td>
                          <td>
                            <button className="picking-print-btn" onClick={() => handlePrint(order)}>
                              Print
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Dispatched Tab */}
        {activeTab === 'dispatched' && (
          <div className="picking-table-container">
            {dispatched.length === 0 ? (
              <div className="picking-empty">
                <p className="picking-empty-message">No active pick tickets</p>
                <p className="picking-empty-hint">Dispatched tickets will appear here</p>
              </div>
            ) : (
              <table className="picking-table">
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Units</th>
                    <th>Picker</th>
                    <th>Dispatched</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatched.map(ticket => (
                    <tr key={ticket.id} className={ticket.is_stale ? 'picking-stale-row' : ''}>
                      <td className="picking-order-number">
                        {ticket.ticket_number}
                        {ticket.is_stale && <span className="picking-stale-badge">Stale</span>}
                      </td>
                      <td>{ticket.tran_id}</td>
                      <td className="picking-customer-name">{ticket.customer_name}</td>
                      <td>{ticket.total_line_items || 0}</td>
                      <td>{ticket.total_units || 0}</td>
                      <td>{ticket.operator_name || '-'}</td>
                      <td>
                        {ticket.hours_since_dispatch !== undefined
                          ? `${Math.round(ticket.hours_since_dispatch * 10) / 10}h ago`
                          : '-'}
                      </td>
                      <td>
                        <button className="picking-return-btn" onClick={() => handleReturn(ticket.id)}>
                          Return to Queue
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Print Confirmation Modal */}
        {confirmOrder && (
          <div className="picking-modal-overlay" onClick={() => !printing && setConfirmOrder(null)}>
            <div className="picking-modal" onClick={e => e.stopPropagation()}>
              <h2>Print Pick Ticket</h2>
              <div className="picking-modal-detail">
                <span className="picking-modal-detail-label">Order</span>
                <span className="picking-modal-detail-value">{confirmOrder.tran_id}</span>
              </div>
              <div className="picking-modal-detail">
                <span className="picking-modal-detail-label">Customer</span>
                <span className="picking-modal-detail-value">{confirmOrder.customer_name}</span>
              </div>
              <div className="picking-modal-detail">
                <span className="picking-modal-detail-label">Line Items</span>
                <span className="picking-modal-detail-value">{confirmOrder.total_line_items || 0}</span>
              </div>
              <div className="picking-modal-detail">
                <span className="picking-modal-detail-label">Shippable</span>
                <span className="picking-modal-detail-value">
                  {confirmOrder.shippable_line_items || 0} of {confirmOrder.total_line_items || 0}
                </span>
              </div>
              {confirmOrder.ship_date && (
                <div className="picking-modal-detail">
                  <span className="picking-modal-detail-label">Ship Date</span>
                  <span className="picking-modal-detail-value">
                    {new Date(confirmOrder.ship_date).toLocaleDateString('en-US')}
                  </span>
                </div>
              )}
              <div className="picking-modal-actions">
                <button className="picking-modal-cancel" onClick={() => setConfirmOrder(null)} disabled={printing}>
                  Cancel
                </button>
                <button className="picking-modal-confirm" onClick={confirmPrint} disabled={printing}>
                  {printing ? 'Printing...' : 'Print Ticket'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PickingStation;
